import { PORTAL_CONFIG_RULES as rules } from './dynamicConfigConstants';

const getCfgVal = (configurations, key) => {
    const entry = (configurations || []).find(c => c.key === key);
    return entry !== undefined ? entry.value : undefined;
};

// ── Query helpers (for UI filtering) ──────────────────────────────────────

export const getAllowedSectionTypesForTask = (taskType) => {
    if (!taskType) return null;
    return rules.taskTypeRules[taskType]?.allowedSectionTypes || null;
};

export const getAllowedUiComponentsForSection = (sectionType) => {
    if (!sectionType) return null;
    return rules.sectionTypeRules[sectionType]?.allowedUiComponents || null;
};

// Internal types a task allows for its fields (null = no task-level restriction)
export const getAllowedFieldInternalTypesForTask = (taskType) => {
    if (!taskType) return null;
    return rules.taskTypeRules[taskType]?.requiredFieldInternalType?.values || null;
};

// Returns forced field values for a given task type (single required value = lock the field)
export const getForcedFieldValues = (taskType) => {
    if (!taskType) return null;
    const taskRule = rules.taskTypeRules[taskType];
    if (!taskRule) return null;
    const result = {};
    if (taskRule.requiredFieldInternalType?.values?.length === 1) {
        result.internalFieldType = taskRule.requiredFieldInternalType.values[0];
    }
    if (taskRule.requiredFieldUiComponent?.values?.length === 1) {
        result.uicomponentType = taskRule.requiredFieldUiComponent.values[0];
    }
    return Object.keys(result).length ? result : null;
};

// ── Rule checks ────────────────────────────────────────────────────────────

const checkField = (field, sectionRule, sectionType, taskType) => {
    const errs = {};
    const warns = {};

    // uicomponentType allowed by section type
    if (sectionRule?.allowedUiComponents) {
        const { values, level, message } = sectionRule.allowedUiComponents;
        if (values.length > 0 && field.uicomponentType && !values.includes(field.uicomponentType)) {
            if (level === 'error') errs.uicomponentType = message;
            else warns.uicomponentType = message;
        }
    }

    // internalFieldType required/restricted by section type
    if (sectionRule?.allowedInternalTypes) {
        const { values, level, message } = sectionRule.allowedInternalTypes;
        if (!field.internalFieldType) {
            if (level === 'error' && sectionRule.requireInternalType) {
                errs.internalFieldType = 'Internal field type is required for this section type';
            }
        } else if (!values.includes(field.internalFieldType)) {
            if (level === 'error') errs.internalFieldType = message;
            else warns.internalFieldType = message;
        }
    }

    const uiRule = rules.uiComponentRules[field.uicomponentType];
    if (uiRule) {
        // Required configs
        if (uiRule.requiredConfigs) {
            const { values, level, message } = uiRule.requiredConfigs;
            const anyMissing = values.some(cfgKey => {
                if (cfgKey.includes('|')) {
                    return cfgKey.split('|').every(k => getCfgVal(field.configurations, k) === undefined);
                }
                return getCfgVal(field.configurations, cfgKey) === undefined;
            });
            if (anyMissing) {
                if (level === 'error') errs._cfgRequired = message;
                else warns._cfgRequired = message;
            }
        }

        // Recommended internal types for this UI component
        if (uiRule.recommendedInternalTypes && field.internalFieldType) {
            const { values, level, message } = uiRule.recommendedInternalTypes;
            if (!values.includes(field.internalFieldType)) {
                if (level === 'error') errs.internalFieldType = errs.internalFieldType || message;
                else warns.internalFieldType = warns.internalFieldType || message;
            }
        }

        // Recommended section types for this UI component
        if (uiRule.recommendedSectionTypes && sectionType) {
            const { values, level, message } = uiRule.recommendedSectionTypes;
            if (!values.includes(sectionType)) {
                if (level === 'error') errs.uicomponentType = errs.uicomponentType || message;
                else warns.uicomponentType = warns.uicomponentType || message;
            }
        }
    }

    // Task-level field constraints (e.g. UPLOAD_DOCUMENTS limits fields to client portal file types)
    if (taskType) {
        const taskRule = rules.taskTypeRules[taskType];
        if (taskRule?.requiredFieldInternalType) {
            const { values, level, message } = taskRule.requiredFieldInternalType;
            if (field.internalFieldType && !values.includes(field.internalFieldType)) {
                if (level === 'error') errs.internalFieldType = errs.internalFieldType || message;
                else warns.internalFieldType = warns.internalFieldType || message;
            }
        }
        if (taskRule?.requiredFieldUiComponent) {
            const { values, level, message } = taskRule.requiredFieldUiComponent;
            if (field.uicomponentType && !values.includes(field.uicomponentType)) {
                if (level === 'error') errs.uicomponentType = errs.uicomponentType || message;
                else warns.uicomponentType = warns.uicomponentType || message;
            }
        }
    }

    return { errs, warns };
};

const checkSection = (section, parentTaskType) => {
    const errs = {};
    const warns = {};

    const sectionType = getCfgVal(section.configurations, 'sectionType');

    // task type → section type constraint
    if (parentTaskType && sectionType) {
        const taskRule = rules.taskTypeRules[parentTaskType];
        if (taskRule?.allowedSectionTypes) {
            const { values, level, message } = taskRule.allowedSectionTypes;
            if (!values.includes(sectionType)) {
                if (level === 'error') errs.sectionType = message;
                else warns.sectionType = message;
            }
        }
    }

    const sectionRule = sectionType ? rules.sectionTypeRules[sectionType] : null;
    const fieldsErrs = {};
    const fieldsWarns = {};

    (section.fields || []).forEach((field, fi) => {
        const { errs: fErrs, warns: fWarns } = checkField(field, sectionRule, sectionType, parentTaskType);
        if (Object.keys(fErrs).length) fieldsErrs[fi] = fErrs;
        if (Object.keys(fWarns).length) fieldsWarns[fi] = fWarns;
    });

    if (Object.keys(fieldsErrs).length) errs.fields_map = fieldsErrs;
    if (Object.keys(fieldsWarns).length) warns.fields_map = fieldsWarns;

    return { errs, warns };
};

// ── Main export ────────────────────────────────────────────────────────────

export const runRules = (sectionList, taskList) => {
    const ruleErrors = { sections: {}, tasks: {} };
    const ruleWarnings = { sections: {}, tasks: {} };

    sectionList.forEach((section, si) => {
        const { errs, warns } = checkSection(section, null);
        if (Object.keys(errs).length) ruleErrors.sections[si] = errs;
        if (Object.keys(warns).length) ruleWarnings.sections[si] = warns;
    });

    taskList.forEach((task, ti) => {
        const taskErrs = {};
        const taskWarns = {};
        const sectionsErrs = {};
        const sectionsWarns = {};

        (task.sections || []).forEach((section, si) => {
            const { errs, warns } = checkSection(section, task.type);
            if (Object.keys(errs).length) sectionsErrs[si] = errs;
            if (Object.keys(warns).length) sectionsWarns[si] = warns;
        });

        if (Object.keys(sectionsErrs).length) taskErrs.sections_map = sectionsErrs;
        if (Object.keys(sectionsWarns).length) taskWarns.sections_map = sectionsWarns;

        if (Object.keys(taskErrs).length) ruleErrors.tasks[ti] = taskErrs;
        if (Object.keys(taskWarns).length) ruleWarnings.tasks[ti] = taskWarns;
    });

    return { ruleErrors, ruleWarnings };
};

// Deep-merge two error/warning objects (b wins on string conflicts)
export const mergeIssues = (a, b) => {
    if (!b || !Object.keys(b).length) return a;
    if (!a || !Object.keys(a).length) return b;
    const result = { ...a };
    Object.entries(b).forEach(([k, v]) => {
        if (v && typeof v === 'object' && !Array.isArray(v) && typeof result[k] === 'object') {
            result[k] = mergeIssues(result[k], v);
        } else {
            result[k] = v;
        }
    });
    return result;
};

// Flatten warnings/errors into a list for summary display
export const flattenIssues = (issues, sectionList, taskList) => {
    const list = [];

    const fieldLabel = (fields, fi) => fields?.[fi]?.label || `Field ${+fi + 1}`;
    const sectionLabel = (sections, si) => sections?.[si]?.label || `Section ${+si + 1}`;

    const collectFieldIssues = (fieldsMap, fields, prefix) => {
        Object.entries(fieldsMap || {}).forEach(([fi, fIssue]) => {
            const fLabel = fieldLabel(fields, fi);
            Object.entries(fIssue).forEach(([, msg]) => {
                if (typeof msg === 'string') list.push({ path: `${prefix} / ${fLabel}`, message: msg });
            });
        });
    };

    Object.entries(issues.sections || {}).forEach(([si, sIssue]) => {
        const sLabel = sectionLabel(sectionList, si);
        if (sIssue.sectionType) list.push({ path: sLabel, message: sIssue.sectionType });
        collectFieldIssues(sIssue.fields_map, sectionList[+si]?.fields, sLabel);
    });

    Object.entries(issues.tasks || {}).forEach(([ti, tIssue]) => {
        const tLabel = taskList[+ti]?.name || `Task ${+ti + 1}`;
        Object.entries(tIssue.sections_map || {}).forEach(([si, sIssue]) => {
            const sLabel = sectionLabel(taskList[+ti]?.sections, si);
            const prefix = `${tLabel} / ${sLabel}`;
            if (sIssue.sectionType) list.push({ path: prefix, message: sIssue.sectionType });
            collectFieldIssues(sIssue.fields_map, taskList[+ti]?.sections?.[+si]?.fields, prefix);
        });
    });

    return list;
};
